/**
 * action_queue — LOCKED: always on the Overview, always first.
 *
 * One panel where the old Home drew four regions: the time-off banner, Action
 * Required, the Morning Brief and the Work Finished queue. Top to bottom:
 *
 *   1. ONE recap line from GET /briefing/today — counts only.
 *   2. The alert cards (buildActionAlerts), failures first.
 *   3. The jobs a crew has reported finished (WorkFinishedQueue).
 *
 * NO AI TEXT. The brief row carries `lastNightWrap.summary`, which the model
 * wrote; it is not shown here. The recap line is the brief's FACTS (job,
 * ticket and pending-review counts), which the API counted.
 *
 * The brief's finished-job LIST is gone from this page on purpose: every job a
 * crew reported done appeared there and again in the Work Finished queue. The
 * recap keeps the count; the queue keeps the rows.
 *
 * THE CASING READS ARE ASKED ONLY OF A CASING TENANT. Jobs, tickets, the
 * ticket dashboard and the brief recap (its counts are casing jobs and
 * tickets) are casing. A tenant without casing (surface rentals, trucking,
 * downhole, pressure testing) was refused GET /tickets and
 * /tickets/summary/dashboard (403, "casing_running vertical not enabled") and
 * its admin saw "You don't have permission to view tickets" on the Overview.
 * An admin never sees a permission message for a vertical the company does
 * not have. A positive guard: casing, alone or with any other vertical, asks
 * every read it did before, and its Overview is unchanged.
 *
 * EVERY OTHER VERTICAL'S ITEMS COME FROM GET /dashboard/action-queue
 * (api#1116): downhole (incl. Directional & MWD), trucking, surface rentals,
 * pressure testing and NDT (api#1142 — the API gates NDT items on the
 * company's NDT DATA; this side asks when the company has the vertical). It is asked only when the company has one of those — a
 * casing-only tenant (Smith) makes no new request and draws no new card — and
 * its cards follow the casing ones: failures first, then items, worst first.
 * "Nothing needs action" needs the route to have ARRIVED, with no failures and
 * a non-empty `checked`; a route that checked nothing for this caller is not
 * a clean bill (buildServerActionAlerts).
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../../api/client';
import { unwrap } from '../../../lib/api-envelope';
import { useAuth } from '../../../contexts/AuthContext';
import { useTenantTimeZone } from '../../../hooks/useTenantTimeZone';
import { useTenantCurrency } from '../../../contexts/LocaleContext';
import { ndtTenantQuoteCurrency } from '../../../lib/ndt-quotes';
import { todayCalendarDate, formatCalendarDate } from '../../../lib/calendar-date';
import { queryLoadFailure } from '../../../lib/load-failure';
import { briefIsStale, isBriefAudience } from '../../../lib/morning-brief';
import type { StoredBrief } from '../../../types/morning-brief';
import OpsAlerts from '../../dashboard/OpsAlerts';
import WorkFinishedQueue from '../../dashboard/WorkFinishedQueue';
import { WidgetPanel } from '../WidgetPanel';
import { buildActionAlerts } from '../buildActionAlerts';
import { buildServerActionAlerts } from '../buildServerActionAlerts';
import { ACTION_QUEUE_VERTICALS } from '../../../types/action-queue';
import {
  useOverviewEquipCerts,
  useOverviewEquipment,
  useOverviewInvoiceDash,
  useOverviewJobs,
  useOverviewQuoteDash,
  useOverviewSafetyDash,
  useOverviewTeamCertSummary,
  useOverviewTicketDash,
  useOverviewTickets,
  useOverviewTimeOff,
  useActionQueue,
} from '../overviewQueries';

/** Only a read that never arrived is a failure; a refetch that fails keeps the last answer. */
const failureOf = (q: { data: unknown; error?: unknown; fetchStatus?: string; failureReason?: unknown }) =>
  q.data === undefined ? queryLoadFailure(q) : null;

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

function BriefRecap() {
  const { user } = useAuth();
  const audience = isBriefAudience((user as any)?.role);
  const q = useQuery({
    queryKey: ['briefing-today'],
    queryFn: () => api.briefing.today().then((r) => unwrap<StoredBrief>(r.data)),
    enabled: audience,
    retry: false,
  });
  if (!audience) return null;

  let text: ReactNode;
  if (q.isLoading) {
    text = <span className="inline-block h-3 w-48 animate-pulse rounded bg-fieldiq-dark/60" aria-hidden="true" />;
  } else if (q.isError) {
    text = 'The morning brief did not load. Nothing below depends on it.';
  } else if (!q.data || !q.data.facts) {
    text = 'No morning brief yet. The first one is written at 07:00.';
  } else {
    const { facts } = q.data;
    const day = formatCalendarDate(q.data.briefDate, { month: 'short', day: 'numeric' }) ?? '—';
    text = (
      <>
        <span className="font-bold">{briefIsStale(q.data) ? `Brief from ${day}` : `Morning brief · ${day}`}:</span>{' '}
        {plural(facts.period.finishedJobCount, 'job')} finished
        {facts.period.finishedWithoutTicket > 0 && `, ${facts.period.finishedWithoutTicket} with no ticket`}
        {' · '}
        <Link to="/invoices/generate" className="underline decoration-fi-amber decoration-2 underline-offset-4">
          {facts.standing.invoiceable} ready to bill
        </Link>
        {facts.pendingAgentRuns.total > 0 && (
          <>
            {' · '}
            <Link to="/agent?panel=activity" className="underline decoration-fi-amber decoration-2 underline-offset-4">
              {facts.pendingAgentRuns.total} waiting on a person
            </Link>
          </>
        )}
      </>
    );
  }

  return (
    <p data-testid="brief-recap" className="border-b border-fieldiq-border px-4 py-2.5 text-xs text-fieldiq-text">
      {text}
    </p>
  );
}

export default function ActionQueueWidget() {
  const { hasVertical } = useAuth();
  const casingReads = hasVertical('casing_running');
  const queueVerticals = ACTION_QUEUE_VERTICALS.filter((v) => hasVertical(v));
  const timeZone = useTenantTimeZone();
  const today = todayCalendarDate(timeZone);
  const quoteCurrency = ndtTenantQuoteCurrency(hasVertical, useTenantCurrency());

  const jobs = useOverviewJobs(casingReads);
  const tickets = useOverviewTickets(casingReads);
  const ticketDash = useOverviewTicketDash(casingReads);
  const equipment = useOverviewEquipment();
  const equipCerts = useOverviewEquipCerts();
  const teamCerts = useOverviewTeamCertSummary();
  const safety = useOverviewSafetyDash();
  const invoices = useOverviewInvoiceDash();
  const quotes = useOverviewQuoteDash();
  const timeOff = useOverviewTimeOff();
  const queue = useActionQueue(queueVerticals.length > 0);

  const reads = [jobs, tickets, ticketDash, equipment, equipCerts, teamCerts, safety, invoices, quotes, queue];
  const loading = reads.some((r) => r.isLoading && failureOf(r) === null);

  const casingAlerts = buildActionAlerts({
    today,
    timeZone,
    jobs: jobs.data ?? [],
    tickets: tickets.data ?? [],
    ticketDash: ticketDash.data ?? {},
    equipment: equipment.data ?? [],
    equipCerts: equipCerts.data ?? [],
    teamCertSummary: teamCerts.data ?? {},
    safetyDash: safety.data ?? {},
    invoiceDash: invoices.data ?? {},
    quoteDash: quotes.data ?? {},
    quoteCurrency,
    timeOff: timeOff.data ?? {},
    failures: {
      jobs: failureOf(jobs),
      tickets: failureOf(tickets),
      ticketDash: failureOf(ticketDash),
      equipment: failureOf(equipment),
      equipCerts: failureOf(equipCerts),
      teamCerts: failureOf(teamCerts),
      safety: failureOf(safety),
      invoices: failureOf(invoices),
      quotes: failureOf(quotes),
    },
  });
  const serverAlerts = queueVerticals.length > 0
    ? buildServerActionAlerts({ queue: queue.data, failure: failureOf(queue), verticals: queueVerticals })
    : [];
  // Failures first across both sources, then the casing cards, then the rest.
  const alerts = [
    ...casingAlerts.filter((a) => a.failure),
    ...serverAlerts.filter((a) => a.failure),
    ...casingAlerts.filter((a) => !a.failure),
    ...serverAlerts.filter((a) => !a.failure),
  ];
  // The route ran, but no item applies to this caller's role: its silence is
  // not a finding, so the empty line must not claim one for those verticals.
  // Said only when the route SERVED a vertical: NDT is data-gated
  // (api#1142), so an NDT-only company with no NDT rows gets `verticals: []`
  // and nothing to check — that is "nothing needs action", not a role gap.
  const queueCheckedNothing = queueVerticals.length > 0 && !!queue.data
    && (queue.data.verticals ?? []).length > 0 && (queue.data.checked ?? []).length === 0;

  return (
    <WidgetPanel
      widgetKey="action_queue"
      eyebrow="Needs you"
      title="Action Queue"
      className="border-l-4 border-l-fi-amber"
      aside={
        alerts.length > 0 ? (
          <span
            data-testid="action-queue-count"
            className="rounded-fi-pill border border-black/20 bg-fi-signal-amber px-2 py-0.5 text-xs font-black text-fi-signal-ink"
          >
            {alerts.length}
          </span>
        ) : undefined
      }
    >
      {casingReads && <BriefRecap />}
      <div className="space-y-4 p-4">
        {alerts.length > 0 ? (
          <OpsAlerts alerts={alerts} bare />
        ) : loading ? (
          <div className="h-12 animate-pulse rounded-fi-control bg-fieldiq-dark/60" aria-busy="true" />
        ) : (
          // Reached only when every read ARRIVED: a failed one is a card above.
          <p data-testid="action-queue-empty" className="py-4 text-center text-sm text-fieldiq-muted">
            {queueCheckedNothing
              ? 'Nothing here needs action. No checks apply to your role.'
              : 'Nothing needs action right now.'}
          </p>
        )}
        <WorkFinishedQueue bare />
      </div>
    </WidgetPanel>
  );
}
